/**
 * ProjectDashboard — manager role dashboard for Project/Delivery managers.
 *
 * Built from /tmp/aura-design/extracted/aura-web-app/project/aura-dash-3.jsx
 * (`ProjectDashboard`). UI-only: no backend changes. Uses design tokens
 * already shipped into `src/index.css` (Phase 1 of UI rebuild). Every widget
 * handles loading / error / empty independently.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plus,
  Sparkles,
  AlertTriangle,
  Loader2,
  CalendarDays,
} from 'lucide-react';
import { api } from '@/utils/api';

/* ------------------------------------------------------------------ */
/*  Local helpers                                                      */
/* ------------------------------------------------------------------ */

function compactINR(amount: number | null | undefined): string {
  if (amount == null || !Number.isFinite(amount)) return '\u2014';
  const abs = Math.abs(amount);
  const sign = amount < 0 ? '-' : '';
  if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
  if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
  if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)} K`;
  return `${sign}₹${Math.round(abs).toLocaleString('en-IN')}`;
}

function formatDateShort(d: string | Date | null | undefined): string {
  if (!d) return '\u2014';
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (!(dt instanceof Date) || isNaN(dt.getTime())) return '\u2014';
  return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
}

type FetchState<T> = { data: T | null; loading: boolean; error: string | null };

function useFetch<T>(url: string | null): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    data: null,
    loading: !!url,
    error: null,
  });
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    setState({ data: null, loading: true, error: null });
    api
      .get(url)
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.data !== undefined ? res.data.data : res.data;
        setState({ data: payload as T, loading: false, error: null });
      })
      .catch((err) => {
        if (cancelled) return;
        const msg =
          err?.response?.status === 404
            ? 'Not available yet'
            : err?.message ?? 'Error';
        setState({ data: null, loading: false, error: msg });
      });
    return () => {
      cancelled = true;
    };
  }, [url]);
  return state;
}

/* ------------------------------------------------------------------ */
/*  Atoms                                                              */
/* ------------------------------------------------------------------ */

function KpiCard({
  label,
  value,
  sub,
  tone,
}: {
  label: string;
  value: string;
  sub: string;
  tone?: 'warn' | 'ok' | 'danger';
}) {
  const subColor =
    tone === 'warn'
      ? 'var(--warn)'
      : tone === 'danger'
        ? 'var(--danger)'
        : tone === 'ok'
          ? 'var(--forest-600)'
          : 'var(--ink-3)';
  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 4,
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontFamily: 'var(--ff-serif, serif)',
          fontSize: 26,
          fontWeight: 500,
          color: 'var(--ink-0)',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: subColor, marginTop: 2 }}>{sub}</div>
    </div>
  );
}

function LoadingPill() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: 'var(--ink-3)',
      }}
    >
      <Loader2 size={12} className="animate-spin" /> Loading…
    </span>
  );
}

function ErrorPill({ msg = 'Unavailable' }: { msg?: string }) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontSize: 11,
        color: 'var(--danger)',
      }}
    >
      <AlertTriangle size={12} /> {msg}
    </span>
  );
}

function EmptyState({ msg }: { msg: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        color: 'var(--ink-3)',
        fontStyle: 'italic',
        padding: 12,
        textAlign: 'center',
      }}
    >
      {msg}
    </div>
  );
}

function ProgressBar({
  value,
  color = 'var(--ink-0)',
  h = 4,
  bg = 'var(--ivory-2)',
}: {
  value: number;
  color?: string;
  h?: number;
  bg?: string;
}) {
  const pct = Math.max(0, Math.min(100, value));
  return (
    <div
      style={{
        background: bg,
        borderRadius: 999,
        height: h,
        overflow: 'hidden',
        marginTop: 4,
      }}
    >
      <div
        style={{
          width: `${pct}%`,
          height: '100%',
          background: color,
          borderRadius: 999,
        }}
      />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Gantt strip                                                        */
/* ------------------------------------------------------------------ */

interface ProjectRow {
  id: string;
  name: string;
  status?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  progress?: number | null;
  budget?: number | string | null;
  account?: { name?: string } | null;
  owner?: { firstName?: string; lastName?: string } | null;
}

function GanttStrip() {
  const q = useFetch<{ data: ProjectRow[] } | ProjectRow[]>(
    '/projects?status=active&limit=10'
  );

  const projects: ProjectRow[] = useMemo(() => {
    if (!q.data) return [];
    return (Array.isArray(q.data) ? q.data : q.data.data ?? []).slice(0, 10);
  }, [q.data]);

  // Compute date window: min(start), max(end) across all projects.
  const { minMs, maxMs, todayPct } = useMemo(() => {
    if (projects.length === 0) {
      return { minMs: 0, maxMs: 0, todayPct: 0 };
    }
    let min = Infinity;
    let max = -Infinity;
    for (const p of projects) {
      if (p.startDate) min = Math.min(min, new Date(p.startDate).getTime());
      if (p.endDate) max = Math.max(max, new Date(p.endDate).getTime());
    }
    if (!isFinite(min) || !isFinite(max) || max <= min) {
      return { minMs: 0, maxMs: 0, todayPct: 0 };
    }
    const today = Date.now();
    const tp = ((today - min) / (max - min)) * 100;
    return { minMs: min, maxMs: max, todayPct: Math.max(0, Math.min(100, tp)) };
  }, [projects]);

  const toPct = (iso: string | null | undefined) => {
    if (!iso || maxMs === minMs) return 0;
    const t = new Date(iso).getTime();
    return Math.max(0, Math.min(100, ((t - minMs) / (maxMs - minMs)) * 100));
  };

  const statusColor = (s?: string | null) => {
    if (!s) return 'var(--forest)';
    const v = s.toLowerCase();
    if (v === 'at-risk' || v === 'at_risk') return 'var(--saffron)';
    if (v === 'delayed' || v === 'blocked') return 'var(--danger)';
    return 'var(--forest)';
  };

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        overflow: 'hidden',
      }}
    >
      <div
        style={{
          padding: '14px 16px',
          borderBottom: '1px solid var(--line)',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          Active projects · Gantt
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 10, fontSize: 11 }}>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                background: 'var(--forest)',
                marginRight: 4,
              }}
            />
            On track
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                background: 'var(--saffron)',
                marginRight: 4,
              }}
            />
            At risk
          </span>
          <span>
            <span
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                borderRadius: 2,
                background: 'var(--danger)',
                marginRight: 4,
              }}
            />
            Delayed
          </span>
        </div>
      </div>

      <div style={{ padding: 16 }}>
        {q.loading && <LoadingPill />}
        {q.error && <ErrorPill />}
        {!q.loading && !q.error && projects.length === 0 && (
          <EmptyState msg="No active projects yet" />
        )}
        {projects.length > 0 && (
          <>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '260px 1fr',
                gap: 0,
                marginBottom: 8,
                borderBottom: '1px solid var(--line)',
                paddingBottom: 6,
                fontSize: 11,
                color: 'var(--ink-3)',
              }}
            >
              <div>Project</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span>{formatDateShort(new Date(minMs))}</span>
                <span>{formatDateShort(new Date(maxMs))}</span>
              </div>
            </div>
            {projects.map((p, i) => {
              const startP = toPct(p.startDate);
              const endP = toPct(p.endDate);
              const width = Math.max(2, endP - startP);
              const color = statusColor(p.status);
              const progress = Number(p.progress ?? 0);
              const ownerName = p.owner
                ? `${p.owner.firstName ?? ''} ${p.owner.lastName ?? ''}`.trim()
                : '';
              return (
                <Link
                  key={p.id}
                  to={`/projects/${p.id}`}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '260px 1fr',
                    gap: 0,
                    alignItems: 'center',
                    padding: '10px 0',
                    borderBottom:
                      i < projects.length - 1
                        ? '1px solid var(--ivory-3)'
                        : 'none',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  <div style={{ paddingRight: 12 }}>
                    <div
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {p.name}
                    </div>
                    <div
                      style={{
                        fontSize: 11,
                        color: 'var(--ink-3)',
                        marginTop: 2,
                      }}
                    >
                      {[ownerName, p.budget ? compactINR(Number(p.budget)) : null]
                        .filter(Boolean)
                        .join(' · ') || '\u2014'}
                    </div>
                  </div>
                  <div style={{ position: 'relative', height: 28 }}>
                    {[20, 40, 60, 80].map((x) => (
                      <div
                        key={x}
                        style={{
                          position: 'absolute',
                          left: `${x}%`,
                          top: 0,
                          bottom: 0,
                          width: 1,
                          borderLeft: '1px dashed var(--ivory-3)',
                        }}
                      />
                    ))}
                    <div
                      style={{
                        position: 'absolute',
                        left: `${todayPct}%`,
                        top: -4,
                        bottom: -4,
                        width: 1,
                        background: 'var(--ink-0)',
                        opacity: 0.3,
                      }}
                    />
                    <div
                      style={{
                        position: 'absolute',
                        left: `${startP}%`,
                        width: `${width}%`,
                        top: 5,
                        height: 18,
                        borderRadius: 4,
                        background: `linear-gradient(90deg, ${color}dd, ${color})`,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <div
                        style={{
                          position: 'absolute',
                          left: 0,
                          top: 0,
                          bottom: 0,
                          width: `${progress}%`,
                          background: 'rgba(255,255,255,.2)',
                          borderRadius: '4px 0 0 4px',
                        }}
                      />
                      <span
                        style={{
                          position: 'relative',
                          zIndex: 1,
                          fontSize: 10,
                          color: '#fff',
                          padding: '0 8px',
                          fontWeight: 500,
                        }}
                      >
                        {progress > 0 ? `${Math.round(progress)}%` : ''}
                      </span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tasks kanban snapshot                                              */
/* ------------------------------------------------------------------ */

interface TaskRow {
  id: string;
  title: string;
  priority?: string | null;
  dueDate?: string | null;
  status?: string | null;
}

function TaskColumn({
  title,
  color,
  url,
}: {
  title: string;
  color: string;
  url: string;
}) {
  const q = useFetch<{ data: TaskRow[] } | TaskRow[]>(url);
  const tasks: TaskRow[] = useMemo(() => {
    if (!q.data) return [];
    return (Array.isArray(q.data) ? q.data : q.data.data ?? []).slice(0, 6);
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-0)',
        borderRadius: 6,
        border: '1px solid var(--ivory-3)',
        padding: 6,
      }}
    >
      <div
        style={{
          padding: '4px 6px 6px',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: 3,
            background: color,
          }}
        />
        <span style={{ fontSize: 11, fontWeight: 500 }}>{title}</span>
        <span
          style={{
            fontSize: 10,
            color: 'var(--ink-3)',
            marginLeft: 'auto',
          }}
        >
          {q.loading ? '…' : tasks.length}
        </span>
      </div>
      {q.loading && (
        <div style={{ padding: 6 }}>
          <LoadingPill />
        </div>
      )}
      {q.error && (
        <div style={{ padding: 6 }}>
          <ErrorPill msg="—" />
        </div>
      )}
      {!q.loading && !q.error && tasks.length === 0 && (
        <EmptyState msg="None" />
      )}
      {tasks.map((t) => {
        const pri = (t.priority ?? '').toLowerCase();
        const priBg =
          pri === 'high' || pri === 'urgent'
            ? 'var(--danger-bg)'
            : pri === 'med' || pri === 'medium'
              ? 'var(--saffron-50)'
              : 'var(--ivory-2)';
        const priColor =
          pri === 'high' || pri === 'urgent'
            ? 'var(--danger)'
            : pri === 'med' || pri === 'medium'
              ? 'var(--saffron-600)'
              : 'var(--ink-3)';
        return (
          <Link
            key={t.id}
            to={`/tasks/${t.id}`}
            style={{
              display: 'block',
              background: 'var(--ivory-1)',
              borderRadius: 4,
              padding: 6,
              marginBottom: 4,
              border: '1px solid var(--ivory-3)',
              textDecoration: 'none',
              color: 'inherit',
            }}
          >
            <div
              style={{
                fontSize: 11,
                lineHeight: 1.35,
                marginBottom: 4,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }}
            >
              {t.title}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {pri && (
                <span
                  style={{
                    fontSize: 9,
                    height: 14,
                    padding: '0 4px',
                    background: priBg,
                    color: priColor,
                    borderRadius: 3,
                    textTransform: 'lowercase',
                    display: 'inline-flex',
                    alignItems: 'center',
                  }}
                >
                  {pri}
                </span>
              )}
              {t.dueDate && (
                <span style={{ fontSize: 9, color: 'var(--ink-3)' }}>
                  {formatDateShort(t.dueDate)}
                </span>
              )}
            </div>
          </Link>
        );
      })}
    </div>
  );
}

function TasksKanbanSnapshot() {
  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 12,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          Tasks · Today / Upcoming / Overdue
        </div>
        <div style={{ flex: 1 }} />
        <Link
          to="/tasks"
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textDecoration: 'none',
          }}
        >
          View all →
        </Link>
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 8,
        }}
      >
        <TaskColumn
          title="Today"
          color="var(--saffron)"
          url="/tasks?bucket=today&limit=6"
        />
        <TaskColumn
          title="Upcoming"
          color="var(--info)"
          url="/tasks?bucket=upcoming&limit=6"
        />
        <TaskColumn
          title="Overdue"
          color="var(--danger)"
          url="/tasks?bucket=overdue&limit=6"
        />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Project P&L snapshot                                               */
/* ------------------------------------------------------------------ */

interface PnlRow {
  projectId: string;
  projectName: string;
  budgeted: number;
  actual: number;
}

function ProjectPnlSnapshot() {
  const q = useFetch<PnlRow[] | { data: PnlRow[] }>(
    '/projects/pnl-summary?limit=5'
  );
  const rows: PnlRow[] = useMemo(() => {
    if (!q.data) return [];
    return (Array.isArray(q.data) ? q.data : q.data.data ?? []).slice(0, 5);
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 12,
        }}
      >
        Project P&amp;L · Top 5
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <EmptyState msg="Not available yet" />}
      {!q.loading && !q.error && rows.length === 0 && (
        <EmptyState msg="No project P&L data yet" />
      )}
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => {
            const pct =
              r.budgeted > 0 ? Math.round((r.actual / r.budgeted) * 100) : 0;
            return (
              <div key={r.projectId}>
                <div
                  style={{ display: 'flex', fontSize: 11, marginBottom: 4 }}
                >
                  <span
                    style={{
                      flex: 1,
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {r.projectName}
                  </span>
                  <span style={{ color: 'var(--ink-3)' }}>
                    {compactINR(r.actual)} / {compactINR(r.budgeted)}
                  </span>
                </div>
                <ProgressBar
                  value={pct}
                  color={pct > 90 ? 'var(--danger)' : 'var(--ink-0)'}
                  h={3}
                />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Time heatmap                                                       */
/* ------------------------------------------------------------------ */

interface HeatmapCell {
  member: string;
  days: number[]; // 7 values, Mon–Sun
}

function TimeHeatmap() {
  const q = useFetch<HeatmapCell[] | { data: HeatmapCell[] }>(
    '/tasks/timesheet-heatmap?week=current'
  );
  const rows: HeatmapCell[] = useMemo(() => {
    if (!q.data) return [];
    return (Array.isArray(q.data) ? q.data : q.data.data ?? []).slice(0, 8);
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.06,
          fontWeight: 500,
          marginBottom: 10,
        }}
      >
        Hours logged · This week
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <EmptyState msg="Not available yet" />}
      {!q.loading && !q.error && rows.length === 0 && (
        <EmptyState msg="No time entries yet" />
      )}
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '90px repeat(7, 1fr)',
              gap: 3,
            }}
          >
            <div />
            {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
              <div
                key={i}
                style={{
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  textAlign: 'center',
                }}
              >
                {d}
              </div>
            ))}
          </div>
          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: 'grid',
                gridTemplateColumns: '90px repeat(7, 1fr)',
                gap: 3,
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--ink-2)',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {r.member}
              </div>
              {(r.days ?? []).slice(0, 7).map((v, j) => {
                const intensity =
                  v === 0 ? 0 : v < 4 ? 0.25 : v < 7 ? 0.55 : 1;
                return (
                  <div
                    key={j}
                    style={{
                      height: 20,
                      borderRadius: 3,
                      background:
                        intensity === 0
                          ? 'var(--ivory-2)'
                          : `rgba(47,122,91,${intensity})`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 9,
                      color: intensity > 0.5 ? '#fff' : 'var(--ink-2)',
                      fontWeight: 500,
                    }}
                  >
                    {v ? v.toFixed(1) : ''}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Milestones this week                                               */
/* ------------------------------------------------------------------ */

interface Milestone {
  id: string;
  name: string;
  dueDate: string;
  projectId: string;
  projectName?: string;
  status?: string;
}

function MilestonesThisWeek() {
  const q = useFetch<Milestone[] | { data: Milestone[] }>(
    '/projects/milestones?window=7'
  );
  const rows: Milestone[] = useMemo(() => {
    if (!q.data) return [];
    return (Array.isArray(q.data) ? q.data : q.data.data ?? []).slice(0, 10);
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--ivory-1)',
        border: '1px solid var(--line)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--ink-3)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          Milestones · Next 7 days
        </div>
        <div style={{ flex: 1 }} />
        <CalendarDays size={12} style={{ color: 'var(--ink-3)' }} />
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <EmptyState msg="Not available yet" />}
      {!q.loading && !q.error && rows.length === 0 && (
        <EmptyState msg="No milestones this week" />
      )}
      {rows.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {rows.map((m) => (
            <Link
              key={m.id}
              to={`/projects/${m.projectId}`}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                background: 'var(--ivory-0)',
                border: '1px solid var(--ivory-3)',
                fontSize: 12,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  color: 'var(--ink-3)',
                  width: 44,
                  flexShrink: 0,
                }}
              >
                {formatDateShort(m.dueDate)}
              </div>
              <div
                style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                <div style={{ fontWeight: 500 }}>{m.name}</div>
                {m.projectName && (
                  <div style={{ fontSize: 10, color: 'var(--ink-3)' }}>
                    {m.projectName}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Tasks overdue drill-in                                             */
/* ------------------------------------------------------------------ */

function OverdueDrilldown() {
  const q = useFetch<TaskRow[] | { data: TaskRow[] }>(
    '/tasks?bucket=overdue&limit=5'
  );
  const tasks: TaskRow[] = useMemo(() => {
    if (!q.data) return [];
    return Array.isArray(q.data) ? q.data : q.data.data ?? [];
  }, [q.data]);

  return (
    <div
      style={{
        background: 'var(--danger-bg)',
        border: '1px solid #EFCFCA',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: 10,
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: 'var(--danger)',
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
          }}
        >
          Tasks overdue
        </div>
        <div style={{ flex: 1 }} />
        <span
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--danger)',
            background: '#fff',
            padding: '2px 8px',
            borderRadius: 999,
          }}
        >
          {q.loading ? '…' : tasks.length}
        </span>
      </div>
      {q.loading && <LoadingPill />}
      {q.error && <ErrorPill />}
      {!q.loading && !q.error && tasks.length === 0 && (
        <div
          style={{ fontSize: 12, color: 'var(--forest-600)', padding: 8 }}
        >
          All clear. No overdue tasks.
        </div>
      )}
      {tasks.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tasks.slice(0, 5).map((t) => (
            <Link
              key={t.id}
              to={`/tasks/${t.id}`}
              style={{
                display: 'flex',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                background: '#fff',
                border: '1px solid #EFCFCA',
                fontSize: 12,
                textDecoration: 'none',
                color: 'inherit',
              }}
            >
              <span
                style={{
                  fontSize: 10,
                  color: 'var(--danger)',
                  width: 44,
                  flexShrink: 0,
                }}
              >
                {formatDateShort(t.dueDate)}
              </span>
              <span
                style={{
                  flex: 1,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {t.title}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  AI Reschedule CTA                                                  */
/* ------------------------------------------------------------------ */

function AiRescheduleCard() {
  return (
    <Link
      to="/ai?context=projects&action=reschedule"
      style={{
        display: 'block',
        textDecoration: 'none',
        color: 'inherit',
        background:
          'linear-gradient(135deg, var(--saffron-50) 0%, var(--ivory-1) 100%)',
        border: '1px solid var(--saffron-100)',
        borderRadius: 10,
        padding: 16,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 6,
        }}
      >
        <Sparkles size={14} style={{ color: 'var(--saffron-600)' }} />
        <span
          style={{
            fontSize: 11,
            textTransform: 'uppercase',
            letterSpacing: 0.06,
            fontWeight: 500,
            color: 'var(--saffron-600)',
          }}
        >
          AI · 5 credits
        </span>
      </div>
      <div style={{ fontSize: 14, fontWeight: 500, marginBottom: 4 }}>
        Re-schedule at-risk project
      </div>
      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
        AI proposes a new delivery date, reassigns slack from healthy tasks,
        and flags critical-path dependencies.
      </div>
    </Link>
  );
}

/* ------------------------------------------------------------------ */
/*  Top KPIs — lightweight derivation from /projects and /tasks        */
/* ------------------------------------------------------------------ */

interface TasksCountStats {
  overdue?: number;
  total?: number;
}

function TopKpis() {
  const projects = useFetch<ProjectRow[] | { data: ProjectRow[] }>(
    '/projects?status=active&limit=100'
  );
  const milestones = useFetch<Milestone[] | { data: Milestone[] }>(
    '/projects/milestones?window=14'
  );
  const overdue = useFetch<TaskRow[] | { data: TaskRow[]; pagination?: unknown } | TasksCountStats>(
    '/tasks?bucket=overdue&limit=1'
  );

  const activeList: ProjectRow[] = useMemo(() => {
    if (!projects.data) return [];
    return Array.isArray(projects.data)
      ? projects.data
      : projects.data.data ?? [];
  }, [projects.data]);

  const plannedBudget = useMemo(
    () => activeList.reduce((s, p) => s + Number(p.budget ?? 0), 0),
    [activeList]
  );

  const activeCount = activeList.length;
  const statusBreakdown = useMemo(() => {
    let onTrack = 0;
    let atRisk = 0;
    let delayed = 0;
    for (const p of activeList) {
      const s = (p.status ?? '').toLowerCase();
      if (s === 'at-risk' || s === 'at_risk') atRisk++;
      else if (s === 'delayed' || s === 'blocked') delayed++;
      else onTrack++;
    }
    return { onTrack, atRisk, delayed };
  }, [activeList]);

  const milestoneList: Milestone[] = useMemo(() => {
    if (!milestones.data) return [];
    return Array.isArray(milestones.data)
      ? milestones.data
      : milestones.data.data ?? [];
  }, [milestones.data]);

  // Derive overdue count from pagination.total if present
  const overdueCount = useMemo(() => {
    const raw = overdue.data as
      | { pagination?: { total?: number } }
      | TaskRow[]
      | null;
    if (!raw) return null;
    if (Array.isArray(raw)) return raw.length;
    const p = (raw as { pagination?: { total?: number } }).pagination;
    if (p && typeof p.total === 'number') return p.total;
    return (raw as { data?: TaskRow[] }).data?.length ?? 0;
  }, [overdue.data]);

  const kpis = [
    {
      l: 'Active projects',
      v: projects.loading ? '…' : String(activeCount),
      s: activeList.length
        ? `${statusBreakdown.onTrack} on track · ${statusBreakdown.atRisk} at risk · ${statusBreakdown.delayed} delayed`
        : 'No active projects',
    },
    {
      l: 'Planned budget',
      v: projects.loading ? '…' : compactINR(plannedBudget),
      s: 'Across active projects',
    },
    {
      l: 'Milestones · next 14d',
      v: milestones.loading ? '…' : String(milestoneList.length),
      s: milestones.error
        ? 'Not available yet'
        : milestoneList.length > 0
          ? 'Upcoming milestones'
          : 'No milestones',
      tone: milestoneList.length > 5 ? ('warn' as const) : undefined,
    },
    {
      l: 'Overdue tasks',
      v:
        overdue.loading
          ? '…'
          : overdueCount != null
            ? String(overdueCount)
            : '\u2014',
      s: 'Past due date',
      tone:
        overdueCount != null && overdueCount > 0
          ? ('danger' as const)
          : ('ok' as const),
    },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(4, 1fr)',
        gap: 16,
        marginBottom: 16,
      }}
    >
      {kpis.map((k, i) => (
        <KpiCard key={i} label={k.l} value={k.v} sub={k.s} tone={k.tone} />
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ProjectDashboard() {
  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div
        style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}
      >
        <div>
          <h2
            style={{
              fontFamily: 'var(--ff-serif, serif)',
              fontSize: 22,
              fontWeight: 500,
              margin: 0,
            }}
          >
            Projects · Delivery
          </h2>
          <div
            style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 2 }}
          >
            Timeline · tasks · P&L · team utilisation
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <Link
          to="/projects/new"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'var(--ink-0)',
            color: 'var(--ivory-0)',
            padding: '6px 12px',
            borderRadius: 6,
            fontSize: 12,
            textDecoration: 'none',
          }}
        >
          <Plus size={13} /> New project
        </Link>
      </div>

      <TopKpis />

      {/* Gantt */}
      <div style={{ marginBottom: 16 }}>
        <GanttStrip />
      </div>

      {/* Tasks kanban */}
      <div style={{ marginBottom: 16 }}>
        <TasksKanbanSnapshot />
      </div>

      {/* Bottom row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr 1fr',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <ProjectPnlSnapshot />
        <TimeHeatmap />
        <MilestonesThisWeek />
      </div>

      {/* Overdue + AI */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '2fr 1fr',
          gap: 16,
        }}
      >
        <OverdueDrilldown />
        <AiRescheduleCard />
      </div>
    </div>
  );
}
